import Skeleton from '@mui/material/Skeleton';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { MonthClosing, RemoteResource } from '../../../../types';
import { useMuiPickersLocale } from '../../../../hooks';
import { activeCountriesSelector, masterDataSelector } from '../../../../state';
import routes from '../../../../config/routes';

export interface ClosingContentProps {
  resource: RemoteResource<MonthClosing[]>;
  accountingMonth: Date;
}

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding: ${prop => prop.theme.spacing(1, 0)};
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${prop => prop.theme.palette.action.hover};
  }
`;

const ItemsContainer = styled(Stack)`
  padding-top: ${prop => prop.theme.spacing(2)};
  padding-bottom: ${prop => prop.theme.spacing(1)};
`;

const IncorporationSkeleton = () => (
  <>
    <Skeleton />
    <Skeleton />
  </>
);

const IncorporationError = () => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" color="error" padding={1} spacing={1}>
      <ErrorIcon color="error" />
      <Typography variant="body1" color="error">
        {t('errors.generics.loadingData')}
      </Typography>
    </Stack>
  );
};

const ClosingContent = ({ resource, accountingMonth }: ClosingContentProps) => {
  const { t } = useTranslation();
  const masterDataResource = useSelector(masterDataSelector);
  const activeCountriesResource = useSelector(activeCountriesSelector);
  const { data, loaded, error } = resource;
  const loading = resource.loading || masterDataResource.loading || activeCountriesResource.loading;
  const locale = useMuiPickersLocale();

  return (
    <ItemsContainer spacing={1}>
      {!loading &&
        !error &&
        loaded &&
        data.map(i => {
          const lastDayOfMonth = new Date(
            accountingMonth.getFullYear(),
            accountingMonth.getMonth() + 1,
            0
          );
          const searchParams = new URLSearchParams({
            countryAcronym: i.country,
            accountingMonth: lastDayOfMonth.toISOString().split('T')[0],
          });

          return (
            <StyledLink
              key={i.country}
              to={`${
                routes.protectedNestedRoutes.monthClosing.index.path
              }?${searchParams.toString()}`}
            >
              <div>{t(`operativeStructure.countries.${i.country}`)}</div>
              <Typography variant="body1" fontWeight="medium">
                {format(i.creationDate, 'P', { locale })}
              </Typography>
            </StyledLink>
          );
        })}
      {!loading && error && <IncorporationError />}
      {loading && <IncorporationSkeleton />}
      {!loading && !error && loaded && data.length === 0 && <div>{t('table.labels.noData')}</div>}
    </ItemsContainer>
  );
};

export default ClosingContent;
